/** @jsxImportSource @emotion/react */

import { forwardRef } from 'react';

import { useTheme } from '@emotion/react';

import { data } from '../Data/index';
import hero_desktop from './hero-desktop.svg';
import hero_mobile from './hero-mobile.svg';
import hero_spacer from './hero-spacer.svg';
import mobile_spacer from './mobile-spacer.svg';

type HeaderProps = {
  initiateQuiz: () => void;
};

export const Header = forwardRef<HTMLDivElement, HeaderProps>(({ initiateQuiz }, ref) => {
  const theme = useTheme();

  return (
    <header
      ref={ref}
      css={{
        minHeight: '100vh',
        paddingBottom: '100px'
      }}
    >
      <div
        css={{
          display: 'flex',
          justifyContent: 'center',

          '&::before': {
            backgroundColor: '#2a7c87',
            backgroundImage: `url(${mobile_spacer})`,
            backgroundPosition: '0 6px',
            backgroundRepeat: 'repeat-x',
            content: '""',
            height: 'calc(1045/957 * 100vw)', // Height and max-height to match picture
            maxHeight: 'calc(100vh - 267px)',
            position: 'absolute',
            top: 0,
            width: '100vw',
            zIndex: -1,

            '@media (min-width: 769px)': {
              backgroundImage: `url(${hero_spacer})`,
              backgroundPosition: '0 0',
              height: 'calc(937/1920 * 100vw)',
              maxHeight: 'calc(100vh - 260px)'

            },

            '@media (min-width: 1920px)': {
              height: 937
            }
          }
        }}
      >
        <picture
          css={{
            display: 'block',
            height: 'calc(1045/957 * 100vw)',
            maxHeight: 'calc(100vh - 260px)',
            position: 'relative',
            zIndex: 1,

            '@media (min-width: 769px)': {
              height: 'calc(937/1920 * 100vw)'
            },

            '@media (min-width: 1920px)': {
              height: 937
            }
          }}
        >
          <source srcSet={hero_desktop} media="(min-width: 769px)" />
          <source srcSet={hero_mobile} />
          <img
            alt=""
            css={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'top center'
            }}
            height="937"
            loading="lazy"
            srcSet={hero_desktop}
            role="presentation"
            width="1920"
          />
        </picture>
      </div>
      <div
        css={{
          margin: '0 auto',
          maxWidth: '70rem',
          textAlign: 'center',
          fontSize: theme.fontSizes[3],
          padding: '0 1.5rem',
          position: 'relative',
          zIndex: 2,

          [`@media (min-width: 640px)`]: {
            fontSize: theme.fontSizes[6]
          }
        }}
      >
        <h1
          css={{
            backgroundColor: 'transparent',
            border: 0,
            color: 'transparent',
            font: '0/0 a',
            textShadow: 'none'
          }}
        >
          {data.dictionary.h1}
        </h1>

        <div
          css={{
            margin: '0 0 1.5rem 0'
          }}
        >
          <p>{data.dictionary.introduction_text}</p>
        </div>
        <button
          css={{
            boxSizing: 'border-box',
            background: theme.color.primary,
            border: '1px solid transparent',
            borderRadius: '2.5rem',
            color: theme.color.white,
            cursor: 'pointer',
            fontFamily: theme.fontFamily.body,
            fontSize: theme.fontSizes[4],
            fontWeight: theme.fontWeights.semibold,
            padding: '1rem 1.5rem',
            textTransform: 'uppercase',
            transition: 'background-color .1s ease-in,border-color .1s ease-in,color .1s ease-in',

            '&:focus, &:hover': {
              background: theme.color.white,
              border: `1px solid ${theme.color.black}`,
              color: theme.color.primary
            },

            '&:focus:not(:focus-visible)': {
              outline: 'none'
            }
          }}
          onClick={initiateQuiz}
        >
          {data.dictionary.take_the_quiz}
        </button>
        <p
          css={{
            margin: '1.5rem auto',
            maxWidth: '438px',
            fontSize: theme.fontSizes[2]
          }}
        >
          It only takes <span css={{ fontWeight: theme.fontWeights.semibold, lineHeight: '1.4' }}>2 minutes</span>! Plus it’s fun and we
          won’t ask you for any personal information (promise!).
        </p>
      </div>
    </header>
  );
});

Header.displayName = 'Header';
